/**
 * Ingestion source freshness.
 *
 * The question this answers is not "is the process running" but "when did
 * this source last give us data" -- a loop can run happily while the vendor
 * returns nothing, and then a green dot is a lie.
 *
 * `idle` is deliberately its own state, not a warning: a Vietnamese feed
 * that is silent at midnight is correct, and colouring that red trains the
 * eye to ignore red.
 */

import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { SourceHealthRow } from '@/api/types'

const STATUS: Record<SourceHealthRow['status'], { dot: string; text: string; label: string }> = {
  live: { dot: 'bg-up', text: 'text-up', label: 'đang chạy' },
  stale: { dot: 'bg-down', text: 'text-down', label: 'quá hạn' },
  never: { dot: 'bg-down', text: 'text-down', label: 'chưa có dữ liệu' },
  error: { dot: 'bg-down', text: 'text-down', label: 'không kiểm tra được' },
  idle: { dot: 'bg-ink-dim', text: 'text-ink-dim', label: 'ngoài giờ' },
}

export function SourceHealth() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'sources'],
    queryFn: () => api.sourceHealth(),
    refetchInterval: 60_000,
  })

  if (isLoading) return <p className="p-3 text-2xs text-ink-dim">Đang tải…</p>
  if (error) return <p className="p-3 text-2xs text-down">Không đọc được tình trạng nguồn</p>

  const rows = data?.sources ?? []
  const broken = rows.filter((r) => r.status !== 'live' && r.status !== 'idle')

  return (
    <div className="p-2">
      {broken.length > 0 && (
        <p className="mb-2 rounded border border-down/40 bg-down/10 px-2 py-1 text-2xs text-down">
          {broken.length} nguồn cần chú ý — không có tín hiệu lúc này có thể do ingestion,
          không phải do thị trường yên.
        </p>
      )}
      <table className="w-full text-2xs">
        <thead>
          <tr className="text-ink-dim">
            <th className="px-2 py-1 text-left font-medium">Nguồn</th>
            <th className="px-2 py-1 text-left font-medium">Tình trạng</th>
            <th className="px-2 py-1 text-right font-medium">Lần cuối</th>
            <th className="px-2 py-1 text-right font-medium">Ngưỡng</th>
            <th className="px-2 py-1 text-left font-medium">Ghi chú</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const style = STATUS[row.status]
            return (
              <tr key={row.key} className="border-t border-border/60">
                <td className="px-2 py-1 text-ink/90">{row.label}</td>
                <td className={`px-2 py-1 ${style.text}`}>
                  <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${style.dot}`} />
                  {style.label}
                </td>
                <td className="px-2 py-1 text-right font-mono text-ink-dim">
                  {formatAge(row.age_seconds)}
                </td>
                <td className="px-2 py-1 text-right font-mono text-ink-dim">
                  {formatDuration(row.max_silence_seconds)}
                </td>
                <td className="px-2 py-1 text-ink-dim">{row.note}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function formatAge(seconds: number | null): string {
  if (seconds === null) return '—'
  return formatDuration(seconds) + ' trước'
}

function formatDuration(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)}s`
  const minutes = seconds / 60
  if (minutes < 90) return `${Math.round(minutes)} phút`
  const hours = minutes / 60
  if (hours < 48) return `${Math.round(hours)} giờ`
  return `${Math.round(hours / 24)} ngày`
}
